import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { IoMdClose } from 'react-icons/io';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { APPLICATIONS, ENVIRONMENTS } from '../../../Constants/constants';
import {
  CONTEXT_ADD_URL,
  CONTEXT_ADD_MANY_URL,
} from '../../../GraphQL/ManageUrls/TRFX/Mutations';
import Papa from 'papaparse';
import { isEmpty, isString } from '../../../Utils/helpers';
import '../../TestResults/TestResults.css';

function AddUrlForm() {
  const [env, setEnvironment] = useState('dev');
  const [application, setApplication] = useState('TRFX');
  const [client, setClient] = useState('');
  const [url, setUrl] = useState('');
  const [response, setResponse] = useState({});
  const [disableAdd, setDisableAdd] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  let user = sessionStorage.getItem('user');

  try {
    user = isString(user) ? JSON.parse(user) : user;
  } catch (e) {
    console.error('Saved user data is invalid', e);
    user = { _id: '61097bfa97ff9f0009fe3954' };
  }

  //State to store the values
  const [values, setValues] = useState([]);

  useEffect(() => {
    if (!isEmpty(response)) {
      setDisableAdd(false);
    }
  }, [response]);

  const validateForm = () => {
    const errors = {};

    if (!client) {
      errors.client = '*Please enter a client';
    } else if (!/^[a-zA-Z0-9-_/]+$/.test(client)) {
      errors.client = '*Please match the requested format';
    }

    if (!env) {
      errors.env = '*Please select an environment';
    }

    if (!application) {
      errors.app = '*Please select an application';
    }

    if (!url) {
      errors.url = '*Please enter an url';
    } else if (
      !/^(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/[\w#!:.?+=&%!\-/])?$/.test(
        url
      )
    ) {
      errors.url = '*Please match the requested format';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const [runAddManyUrlsMutation] = useMutation(CONTEXT_ADD_MANY_URL, {
    onCompleted: (data) => {
      const response = data.contextAddManyUrlsToContext;
      setResponse(response);
      // Reset state
      window.hideMessage();
      setClient('');
      setUrl('');
      setValues([]);
      window.flash(
        `The action is in progress, this could take few minutes. Please check the tree of URLs later. In progress urls: ${response.success}`,
        'success'
      );
    },
    onError: (error) => {
      window.hideMessage();
      window.flash(
        `The action could not be completed. Please review carefully the information provided or try later.`,
        'error'
      );
    },
  });
  const [runMutation] = useMutation(CONTEXT_ADD_URL, {
    onCompleted: () => {
      // Reset state
      window.hideMessage();
      setClient('');
      setUrl('');
      window.flash('Url was added successfully.', 'success');
    },
    onError: (error) => {
      window.hideMessage();
      window.flash(
        `The action could not be completed. Please review carefully the information provided or try later.`,
        'error'
      );
    },
  });

  const onSubmit = (event) => {
    event.preventDefault();

    if (!isEmpty(values)) {
      setDisableAdd(true);
      runAddManyUrlsMutation({
        variables: {
          env,
          client,
          application,
          urls: values,
          user: user._id,
        },
      });
      return;
    }

    if (!validateForm()) {
      window.hideMessage();
      return;
    }

    window.showMessage('Loading', 'loading');
    setClient('');
    setUrl('');

    runMutation({
      variables: {
        env,
        client,
        url,
        application,
      },
    });
  };

  const handleCsvFileChange = (event) => {
    Papa.parse(event.target.files[0], {
      header: false,
      skipEmptyLines: true,
      complete: function (results) {
        const valuesArray = [];
        results.data.map((d) => {
          valuesArray.push(Object.values(d).toString());
          return null; // Adding return value for map function
        });
        setValues(valuesArray);
      },
    });
  };

  return (
    <div className='d-flex justify-content-center'>
      <div className='e-card' style={{ width: '75%' }}>
        <div className='ml-auto'>
          <a
            href='/trfx-run-tests'
            style={{ textDecoration: 'none', color: 'gray' }}
          >
            <IoMdClose size='20' className='mt-2 mr-2' />
          </a>
        </div>
        <div className='e-card-header mt-2' style={{ width: '100%' }}>
          <div className='e-card-header-caption'>
            <div className='e-card-header-title  d-flex justify-content-center'>
              <h5>Add url to context</h5>
            </div>
          </div>
        </div>
        <div className='e-card-content'>
          <form
            noValidate
            id='addUrlForm'
            onSubmit={(event) => onSubmit(event)}
          >
            <div className='d-flex' style={{ columnGap: '18px' }}>
              <div className='form-group'>
                <div className='e-float-input'>
                  <Dropdown
                    id='env'
                    placeholder='Select environment'
                    value={env}
                    options={ENVIRONMENTS}
                    optionLabel='text'
                    optionValue='value'
                    onChange={(e) => setEnvironment(e.value)}
                    className={formErrors.env ? 'p-invalid' : ''}
                  />
                  {formErrors.env && (
                    <small className='p-error'>{formErrors.env}</small>
                  )}
                </div>
              </div>
              <div className='form-group'>
                <div className='e-float-input'>
                  <Dropdown
                    id='application'
                    placeholder='Select application'
                    value={application}
                    options={APPLICATIONS}
                    onChange={(e) => setApplication(e.value)}
                    className={formErrors.app ? 'p-invalid' : ''}
                  />
                  {formErrors.app && (
                    <small className='p-error'>{formErrors.app}</small>
                  )}
                </div>
              </div>
            </div>
            <div className='form-group'>
              <div className='e-float-input'>
                <InputText
                  id='client'
                  type='text'
                  value={client}
                  onChange={(e) => setClient(e.target.value)}
                  placeholder='Client'
                  className={formErrors.client ? 'p-invalid' : ''}
                />
                {formErrors.client && (
                  <small className='p-error'>{formErrors.client}</small>
                )}
              </div>
            </div>
            <div className='form-group'>
              <div className='e-float-input'>
                <InputText
                  id='url'
                  type='text'
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder='URL'
                  className={formErrors.url ? 'p-invalid' : ''}
                />
                {formErrors.url && (
                  <small className='p-error'>{formErrors.url}</small>
                )}
              </div>
            </div>
            <div className='form-group'>
              <input
                type='file'
                accept='.csv'
                onChange={handleCsvFileChange}
                style={{ marginBottom: '1rem' }}
              />
            </div>
            <div className='text-center'>
              <Button
                type='submit'
                label={isEmpty(values) ? 'Add' : 'Add Many'}
                disabled={disableAdd}
                className='p-button-primary'
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddUrlForm;
